import React, { useState, useEffect } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './PageContent.module.css';

/**
 * PageContent — renders a custom page by slug.
 *
 * Fetches from /api/pages/:slug and renders the body text.
 *
 * Usage (in a route like /about, /terms, /privacy):
 *   {features.customPages && <PageContent slug="about" />}
 *
 * @param {string} props.slug — page identifier
 */
const PageContent = ({ slug, className }) => {
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/pages/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Page not found');
        return r.json();
      })
      .then(setPage)
      .catch(e => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [slug]);

  if (isLoading) return <div className={css.loading}>Loading...</div>;
  if (error) return <div className={css.error}>{error}</div>;
  if (!page) return null;

  return (
    <div className={classNames(css.root, className)}>
      <h1 className={css.title}>{page.title}</h1>
      <div className={css.body}>{page.body}</div>
      {page.updatedAt && (
        <p className={css.updated}>Last updated: {new Date(page.updatedAt).toLocaleDateString()}</p>
      )}
    </div>
  );
};

PageContent.defaultProps = { className: null };
PageContent.propTypes = { slug: string.isRequired, className: string };

export default PageContent;
